import styled from "styled-components";
import { Card, DatePicker, Flex, Layout, Select, Typography } from "antd";
import { COLORS } from "../../../utils/colors";

const { Content } = Layout;
const { Title, Text } = Typography;

export const PageLayout = styled(Layout)`
  && {
    height: 100vh;
    background: linear-gradient(
      180deg,
      ${COLORS.pageGradientStart} 0%,
      ${COLORS.pageGradientEnd} 100%
    );
    font-family: "Roboto", sans-serif;
  }
`;

export const InnerLayout = styled(Layout)`
  && {
    background: transparent;
    height: 100vh;
    overflow-x: hidden;
    overflow-y: auto;
  }
`;

export const PageContent = styled(Content)`
  && {
    padding: 8px 28px 20px;
    position: relative;
    overflow-x: hidden;
  }

  &&::before {
    content: '';
    position: absolute;
    top: -160px;
    left: -160px;
    width: 230px;
    height: 230px;
    border-radius: 50%;
    background: rgba(33, 113, 204, 0.1);
    filter: blur(64px);
    pointer-events: none;
  }

  @media (max-width: 575px) {
    && {
      padding: 8px 16px 16px;
    }
  }
`;

export const ModuleLabel = styled(Text)`
  && {
    font-family: "Roboto", sans-serif;
    font-size: 13px;
    color: ${COLORS.textMuted};
    white-space: nowrap;
  }
`;

export const ModuleSelectPill = styled(Select)`
  &&.ant-select {
    min-width: 180px;
    height: 38px;
    font-family: "Roboto", sans-serif;
    font-size: 13px;
  }

  &&.ant-select .ant-select-selector {
    height: 38px !important;
    padding: 0 14px !important;
    border-radius: 10px !important;
    border: 1px solid ${COLORS.border} !important;
    background: ${COLORS.surface} !important;
    box-shadow: none !important;
    cursor: pointer;
  }

  &&.ant-select .ant-select-selection-item {
    line-height: 36px !important;
    font-family: "Roboto", sans-serif;
    font-size: 13px;
    color: ${COLORS.textBody};
  }

  &&.ant-select .ant-select-arrow {
    color: ${COLORS.textSubtle};
    inset-inline-end: 12px;
    font-size: 11px;
  }

  &&.ant-select:hover .ant-select-selector,
  &&.ant-select-focused .ant-select-selector {
    border-color: ${COLORS.border} !important;
    box-shadow: none !important;
  }

  @media (max-width: 991px) {
    &&.ant-select {
      min-width: 0;
      flex: 1 1 45%;
    }
  }
`;

export const YearPickerPill = styled(DatePicker)`
  &&.ant-picker {
    height: 38px;
    border-radius: 10px !important;
    padding: 0 18px;
    border: 1px solid ${COLORS.border} !important;
    background: ${COLORS.surface};
    font-family: "Roboto", sans-serif;
    font-size: 13px;
    color: ${COLORS.textBody};
    box-shadow: none !important;
    cursor: pointer;
    overflow: hidden;
    min-width: 180px;
  }

  &&.ant-picker .ant-picker-input > input {
    font-family: "Roboto", sans-serif;
    font-size: 13px;
    color: ${COLORS.textBody};
    cursor: pointer;
  }

  &&.ant-picker .ant-picker-input > input::placeholder {
    color: ${COLORS.textSubtle};
  }

  &&.ant-picker .ant-picker-suffix {
    color: ${COLORS.textSubtle};
    margin-inline-start: 10px;
    font-size: 14px;
  }

  &&.ant-picker:hover,
  &&.ant-picker-focused {
    border-color: ${COLORS.border} !important;
    box-shadow: none !important;
    border-radius: 10px !important;
  }

  @media (max-width: 991px) {
    &&.ant-picker {
      min-width: 0;
      flex: 1 1 35%;
    }
  }
` as unknown as typeof DatePicker;

export const ChartsRow = styled(Flex)`
  && > * {
    flex: 1 1 calc(50% - 10px);
    min-width: 0;
  }

  @media (max-width: 991px) {
    && {
      flex-direction: column;
    }

    && > * {
      flex: 1 1 100%;
    }
  }
`;

export const ChartCard = styled(Card)`
  && {
    max-width: 100%;
    border: 1px solid ${COLORS.borderCard};
    border-radius: 16px;
    background: ${COLORS.surface};
    box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
  }

  && .ant-card-body {
    padding: 20px 22px 14px;
  }
`;

export const ChartCardHeader = styled(Flex)`
  && {
    margin-bottom: 14px;
  }
`;

export const ChartHeaderLeft = styled(Flex)`
  && {
    min-width: 0;
    flex: 1 1 auto;
  }
`;

export const ChartIconCircle = styled(Flex)`
  && {
    width: 40px;
    height: 40px;
    border-radius: 12px;
    background: ${COLORS.primary};
    color: ${COLORS.textOnPrimary};
    flex-shrink: 0;
  }

  && .anticon {
    font-size: 18px;
    color: ${COLORS.textOnPrimary};
  }
`;

export const ChartTitleBlock = styled(Flex)`
  && {
    min-width: 0;
  }
`;

export const ChartTitle = styled(Text)`
  && {
    font-family: "Roboto", sans-serif;
    font-size: 15px;
    font-weight: 700;
    color: ${COLORS.textBody};
    line-height: 20px;
  }
`;

export const ChartSubtitle = styled(Text)`
  && {
    font-family: "Roboto", sans-serif;
    font-size: 11px;
    color: ${COLORS.textMuted};
    line-height: 16px;
  }
`;

export const ChartValueRow = styled(Flex)`
  && {
    margin-bottom: 6px;
  }
`;

export const ChartValue = styled(Title)`
  && {
    font-family: "Roboto", sans-serif;
    font-size: 30px !important;
    font-weight: 700 !important;
    color: ${COLORS.textBody} !important;
    margin: 0 !important;
    line-height: 34px !important;
  }
`;

export const ChartTotalLabel = styled(Text)`
  && {
    font-family: "Roboto", sans-serif;
    font-size: 12px;
    color: ${COLORS.textMuted};
  }
`;

export const ChartFrame = styled.div`
  position: relative;
  width: 100%;
`;

export const ChartSvg = styled.svg`
  display: block;
  width: 100%;
  max-width: 100%;
  height: auto;
  font-family: "Roboto", sans-serif;
  user-select: none;
  touch-action: none;
`;

export const ChartEmpty = styled(Flex)`
  && {
    height: 220px;
    font-family: "Roboto", sans-serif;
    font-size: 13px;
    color: ${COLORS.textSubtle};
  }
`;

export const ChartTooltip = styled.div<{
  $left: number;
  $top: number;
  $align: "left" | "center" | "right";
}>`
  position: absolute;
  left: ${({ $left }) => $left}%;
  top: ${({ $top }) => $top}%;
  transform: ${({ $align }) =>
    $align === "right"
      ? "translate(-100%, calc(-100% - 12px))"
      : $align === "left"
        ? "translate(0, calc(-100% - 12px))"
        : "translate(-50%, calc(-100% - 12px))"};
  background: ${COLORS.surface};
  border: 1px solid ${COLORS.borderCard};
  border-radius: 10px;
  box-shadow: 0 6px 16px rgba(16, 24, 40, 0.12);
  padding: 8px 12px;
  pointer-events: none;
  white-space: nowrap;
  z-index: 2;
`;

export const ChartTooltipDate = styled(Text)`
  && {
    display: block;
    font-family: "Roboto", sans-serif;
    font-size: 11px;
    font-weight: 600;
    color: ${COLORS.textBody};
    line-height: 16px;
  }
`;

export const ChartTooltipValue = styled(Text)<{ $color: string }>`
  && {
    display: block;
    font-family: "Roboto", sans-serif;
    font-size: 11px;
    font-weight: 500;
    color: ${({ $color }) => $color};
    line-height: 16px;
  }
`;
